import React from "react";
import { css, useTheme } from "@emotion/react";
import pluralize from "pluralize";

import { NOOP } from "core/constants";
import useBreakpoint from "core/hooks/useBreakpoint";
import Dialog from "core/Components/Dialog/Dialog";
import DialogContent from "core/Components/Dialog/DialogContent";
import Button from "core/Components/Buttons/Button";
import Image from "core/Components/Image";
import Cart from "core/Components/Icons/Cart";
import ContentCombine from "core/Components/ContentCombine";
import { isDown, isUp } from "theme/breakpoints";
import placeholderURL from "images/smiling-book-worm.png";
import { useAppState } from "core/contexts/AppContext";
import { DEFAULT_COUNTRY_CODE_CONFIG } from "core/includes/constants";

type Props = {
    productName: string,
    numberOfItems: number,
    totalPrice: number,
    productImage?: string | Image,
    bottomButtonText?: string,
    onClickBottomButton?: () => void,
    onClickTopButton?: () => void,
    onClose?: () => void,
};

const AddedToCartDialog: React.FC<Props> = ({
    productName,
    productImage = {
        id: 0,
        AltText: 'Smiling girl with book',
        URL: placeholderURL,
        FocusPoint: [0, -10],
    },
    numberOfItems,
    totalPrice,
    bottomButtonText = 'Continue Shopping',
    onClickBottomButton = NOOP,
    onClickTopButton = NOOP,
    onClose = NOOP,
}) => {
    const theme = useTheme();
    const { breakpoint } = useBreakpoint();
    const [appState] = useAppState();
    const currency = appState.countryCode.currency || DEFAULT_COUNTRY_CODE_CONFIG.currency;

    const style = css`
        min-width: 785px;
        height: 528px;
        padding-top: 24px !important;
        padding-bottom: 50px !important;
        display: flex;
        justify-content: center;

        ${theme.breakpoints.between('lg', 'xl')} {
            min-width: 677px;
            height: 447px;
            padding-top: 32px !important;
            padding-bottom: 30px !important;
        }

        ${theme.breakpoints.only('md')} {
            min-width: 677px;
            height: 456px;
            padding-top: 32px !important;
            padding-bottom: 37px !important;
        }
        
        ${theme.breakpoints.down('sm')} {
            padding-top: 16px !important;
        }

        ${theme.breakpoints.only('sm')} {
            min-width: 498px;
            height: 351px;
            padding-bottom: 37px !important;
        }

        ${theme.breakpoints.down('xs')} {
            min-width: 337px;
            height: 347px;
            padding-bottom: 21px!important;
            padding-left: 14px!important;
        }
    `;

    const thankYouStyle = css`
        font-weight: ${theme.fonts.weights.bold};
        color: ${theme.colours.shipGray};
    `;

    const dialogContentStyle = css`
        display: flex;
        flex-direction: column;
        flex: 1;
        width: 100%;
    `;

    const itemsToCartMessageStyle = css`
        display: block;
        margin-top: 20px;

        ${theme.breakpoints.between('lg', 'xl')} {
            margin-top: 11px;
        }

        ${theme.breakpoints.down('sm')} {
            margin-top: 10px;
        }
    `;

    const bottomContainerStyle = css`
        display: flex;
        flex-direction: row;
        flex: 1;
        margin-top: 20px;
        text-align: left;

        ${theme.breakpoints.only('xl')} {
            margin-top: 23px;
        }

        ${theme.breakpoints.only('lg')} {
            margin-top: 27px;
        }

        ${theme.breakpoints.only('sm')} {
            margin-top: 16px;
        }
    `;

    const imageContainerStyle = css`
        height: 100%;

        ${theme.breakpoints.up('md')} {
            padding-left: 14px;
            padding-right: 32px;
        }
        
        ${theme.breakpoints.only('sm')} {
            padding-left: 3px;
            padding-right: 18px;
        }

        ${theme.breakpoints.down('xs')} {
            padding-right: 8px;
        }
    `;

    const imageStyle = css`
        height: 100%;
        width: 333px;
        border-radius: 16px;

        ${theme.breakpoints.between('md', 'xl')} {
            width: 288px;
        }

        ${theme.breakpoints.only('sm')} {
            width: 210px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 141px;
        }
    `;

    const bottomRightContainerStyle = css`
        display: flex;
        flex-direction: column;
        flex: 1;
        justify-content: space-between;

        ${theme.breakpoints.up('xxl')} {
            margin-top: 22px;
        }

        ${theme.breakpoints.only( 'xl')} {
            margin-top: 10px;
        }

        ${theme.breakpoints.only( 'lg')} {
            margin-top: 16px;
        }
    `;

    const productNameStyle = ({ isSmallBp = false } = {}) => css`
        color: ${theme.colours.curiousBlue2};
        font-weight: ${theme.fonts.weights.bold};
        font-size: 23px;
        justify-content: start;
        
        ${isSmallBp && css`
            margin-top: 8px;
            justify-content: center;
        `};
    `;

    const priceStyle = css`
        justify-content: flex-start;
        font-size: 18px;
    `;

    const buttonGroupStyle = css`
        display: flex;
        flex-direction: column;
        gap: 11px;

        ${theme.breakpoints.down('sm')} {
            gap: 8px;
        }
    `;

    const buttonStyle = ({ isCheckoutButton = false } = {}) => css`
        position: relative;
        width: 308px;
        height: 60px;
        border-radius: 30px;
        font-size: 20px;
        display: flex;
        align-items: center;
        background-color: ${isCheckoutButton ? theme.colours.curiousBlue2 : theme.colours.gold};
        color: ${isCheckoutButton ? 'white' : theme.colours.shipGray};
        transition: 300ms background-color ease;
        justify-content: space-around;
        font-weight: ${theme.fonts.weights.bold};

        :hover {
            background-color: ${isCheckoutButton ? theme.colours.malibu3 : theme.colours.mySin};
        }

        ${theme.breakpoints.between('md', 'xl')} {
            width: 266px;
            height: 52px;
        }
        
        ${theme.breakpoints.down('sm')} {
            height: 38px;
        }

        ${theme.breakpoints.only('sm')} {
            width: 194px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 159px;
        }
    `;

    const cartStyle = css`
        position: absolute;
        height: 30px;
        width: 30px;
        left: 60px;
        top: 13px;

        path {
            fill: white;
        }

        ${theme.breakpoints.between('md', 'xl')} {
            width: 23px;
            height: 23px;
            left: 48px;
        }

        ${theme.breakpoints.only('sm')} {
            width: 18px;
            height: 18px;
            left: 33px;
            top: 9px;
        }

        ${theme.breakpoints.down('xs')} {
            width: 17px;
            height: 17px;
            left: 26px;
            top: 7px;
        }
    `;

    const numberOfItemsStyle = css`
        color: ${theme.colours.curiousBlue2};
        font-weight: ${theme.fonts.weights.bold};
    `;

    return (
        <Dialog
            extraStyle={style}
            closeButtonVariant={'circle-overflowing'}
            onClose={onClose}
            closeOnOutsideClick
        >
            <DialogContent css={dialogContentStyle} center>
                <ContentCombine css={thankYouStyle} fontSizeType={'3'}>
                    Thank you!
                </ContentCombine>
                <ContentCombine
                    css={itemsToCartMessageStyle}
                    fontSizeType={'13'}
                >
                    <span css={numberOfItemsStyle}>{numberOfItems}</span>&nbsp;
                    {pluralize('booking', numberOfItems)}
                    {isDown(breakpoint, 'sm') && (
                        <br/>
                    )}
                    &nbsp;added to your cart.
                </ContentCombine>
                {isDown(breakpoint, 'xs') && (
                    <ContentCombine
                        css={productNameStyle({ isSmallBp: true })}
                        fontSizeType={'13'}
                    >
                        {productName}
                    </ContentCombine>
                )}
                <div css={bottomContainerStyle}>
                    <div css={imageContainerStyle}>
                        {productImage && (
                            <div css={imageStyle}>
                                <Image
                                    image={productImage}
                                    useFocusPoint={true}
                                />
                            </div>
                        )}
                    </div>
                    <div css={bottomRightContainerStyle}>
                        {isUp(breakpoint, 'sm') && (
                            <ContentCombine
                                css={productNameStyle()}
                                fontSizeType={'13'}
                            >
                                {productName}
                            </ContentCombine>
                        )}
                        <ContentCombine
                            css={priceStyle}
                            fontSizeType={'18'}
                        >
                            {currency}{totalPrice}
                        </ContentCombine>
                        <div css={buttonGroupStyle}>
                            <Button
                                to={'/cart-summary'}
                                css={buttonStyle({ isCheckoutButton: true })}
                                onClick={onClickTopButton}
                            >
                                <Cart css={cartStyle} />
                                <ContentCombine fontSizeType={'27'}>
                                    Check out
                                </ContentCombine>
                            </Button>
                            <Button
                                to={'/events'}
                                css={buttonStyle()}
                                onClick={onClickBottomButton}
                            >
                                <ContentCombine fontSizeType={'27'}>
                                    {bottomButtonText}
                                </ContentCombine>
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

    );
}

export default AddedToCartDialog;
